import {
    BdsGrid,
    BdsIcon,
    BdsLoadingSpinner,
    BdsTable,
    BdsTableBody,
    BdsTableCell,
    BdsTableHeader,
    BdsTableRow,
    BdsTableTh,
    BdsTooltip,
} from 'blip-ds/dist/blip-ds-react/components';
import { TableRowEmptyState } from 'components/TableRowEmptyState';
import { TicketFinishingModal } from 'components/TicketFinishingModal';
import { TransferTicketModal } from 'components/TransferTicketModal';
import { useTrack } from 'hooks/useTrack';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TicketsInAttendanceTableProps, TicketsResponse } from 'typings';
import '../DataBlock.css';
import './styles.scss';

export const TicketsInAttendanceTable: React.FC<TicketsInAttendanceTableProps> = ({
    data,
    isLoading,
    botsInfos,
    openSideBar,
    onTicketChanged,
}: TicketsInAttendanceTableProps) => {
    const { t } = useTranslation();
    const { track } = useTrack();
    const [openModal, setOpenModal] = useState<'finishing' | 'transfer' | null>(null);
    const [selectedTicket, setSelectedTicket] = useState<TicketsResponse['tickets'][number] | null>(null);

    const selectedTicketBotInfo = useMemo(() => {
        if (!selectedTicket) return null;
        return botsInfos.find(botInfo => botInfo.transhipmentSkill === selectedTicket.botId) || null;
    }, [selectedTicket, botsInfos]);

    return (
        <>
            {selectedTicket && selectedTicketBotInfo && (
                <TicketFinishingModal
                    isOpen={openModal === 'finishing'}
                    setOpen={isOpen => setOpenModal(isOpen ? 'finishing' : null)}
                    ticket={selectedTicket}
                    selectedBotInfo={selectedTicketBotInfo}
                    onTicketChanged={onTicketChanged}
                />
            )}
            {selectedTicket && selectedTicketBotInfo && (
                <TransferTicketModal
                    isOpen={openModal === 'transfer'}
                    setOpen={isOpen => setOpenModal(isOpen ? 'transfer' : null)}
                    ticket={selectedTicket}
                    tenantId={selectedTicketBotInfo.tenantId}
                    botId={selectedTicket.botId}
                    installationId={selectedTicketBotInfo.installationId}
                    onTicketChanged={onTicketChanged}
                />
            )}
            {isLoading ? (
                <BdsGrid xxs="12" alignItems="center" justifyContent="center" height="250px" padding="t-5">
                    <BdsLoadingSpinner />
                </BdsGrid>
            ) : (
                <div className="data-block-table data-block-table--attendance">
                    <BdsTable>
                        <BdsTableHeader>
                            <BdsTableRow>
                                <BdsTableTh justifyContent="center">
                                    {t('go-personal-table-column-ticket-number')}
                                </BdsTableTh>
                                <BdsTableTh justifyContent="center">
                                    {t('go-personal-table-column-queue-wait-time')}
                                </BdsTableTh>
                                <BdsTableTh justifyContent="center">
                                    {t('go-personal-table-column-first-response-time')}
                                </BdsTableTh>
                                <BdsTableTh justifyContent="left">{t('go-personal-table-column-contact')}</BdsTableTh>
                                <BdsTableTh justifyContent="left">{t('go-personal-table-column-agent')}</BdsTableTh>
                                <BdsTableTh justifyContent="left">
                                    {t('go-personal-table-column-number-identification')}
                                </BdsTableTh>
                                <BdsTableTh justifyContent="center">{t('go-personal-table-column-action')}</BdsTableTh>
                            </BdsTableRow>
                        </BdsTableHeader>
                        <BdsTableBody>
                            {data.map(ticket => (
                                <BdsTableRow key={ticket.id}>
                                    <BdsTableCell justifyContent="center">#{ticket.number}</BdsTableCell>
                                    <BdsTableCell justifyContent="center">{ticket.queueTime}</BdsTableCell>
                                    <BdsTableCell justifyContent="center">{ticket.firstResponseTime}</BdsTableCell>
                                    <BdsTableCell justifyContent="left">{ticket.customer.name}</BdsTableCell>
                                    <BdsTableCell justifyContent="left">{ticket.attendant.name}</BdsTableCell>
                                    <BdsTableCell justifyContent="left">
                                        {botsInfos.find(botInfo => botInfo.transhipmentSkill === ticket.botId)
                                            ?.installationName || null}
                                    </BdsTableCell>
                                    <BdsTableCell type="custom" justifyContent="center">
                                        <BdsGrid gap="1" alignItems="center" justifyContent="center">
                                            <BdsTooltip
                                                position="left-center"
                                                tooltip-text={t('go-personal-tooltip-finish-ticket')}
                                            >
                                                <BdsIcon
                                                    className="icon-clickable"
                                                    name="checkball"
                                                    size="medium"
                                                    onClick={() => {
                                                        setSelectedTicket(ticket);
                                                        setOpenModal('finishing');
                                                        track('blippacks-employee-monitoring-finalization-ticket');
                                                    }}
                                                />
                                            </BdsTooltip>
                                            <BdsTooltip
                                                position="left-center"
                                                tooltip-text={t('go-personal-tooltip-transfer-ticket')}
                                            >
                                                <BdsIcon
                                                    className="icon-clickable"
                                                    name="transfer"
                                                    size="medium"
                                                    onClick={() => {
                                                        setSelectedTicket(ticket);
                                                        setOpenModal('transfer');
                                                        track('blippacks-employee-monitoring-transfer-ticket');
                                                    }}
                                                />
                                            </BdsTooltip>
                                            <BdsTooltip
                                                position="left-center"
                                                tooltip-text={t('go-personal-tooltip-view-conversation')}
                                            >
                                                <BdsIcon
                                                    className="icon-clickable"
                                                    name="message-ballon"
                                                    size="medium"
                                                    onClick={() => {
                                                        openSideBar(ticket.number, ticket.botId, ticket.customer.name);
                                                    }}
                                                />
                                            </BdsTooltip>
                                        </BdsGrid>
                                    </BdsTableCell>
                                </BdsTableRow>
                            ))}
                        </BdsTableBody>
                    </BdsTable>
                    {data.length === 0 && (
                        <TableRowEmptyState isSearch={false} messageWhenNoItems={t('go-personal-table-empty-state')} />
                    )}
                </div>
            )}
        </>
    );
};
